import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, PlugZap, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const DEFAULT = {
  provider: "mock",
  api_url: "",
  proxy_url: "",
  endpoint_path: "",
  method: "GET",
  auth_type: "none",
  api_username: "",
  api_password: "",
  bearer_token: "",
  api_key_header: "X-API-Key",
  api_key_value: "",
  headers: "",
  response_format: "JSON",
  params: "",
  timeout: 30,
};

export default function Settings() {
  const { user } = useAuth();
  const isSuper = user.role === "superadmin";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(isSuper ? "" : user.company_id);
  const [cfg, setCfg] = useState(DEFAULT);
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState(false);

  const qs = useCallback(() => (isSuper && companyId ? `?company_id=${companyId}` : ""), [isSuper, companyId]);

  const loadCfg = useCallback(() => {
    if (isSuper && !companyId) return;
    api.get(`/config${qs()}`).then((r) => setCfg({ ...DEFAULT, ...r.data })).catch(() => {});
    api.get(`/logs${qs()}`).then((r) => setLogs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, companyId, qs]);

  useEffect(() => {
    if (isSuper) api.get("/companies-options").then((r) => setCompanies(r.data)).catch(() => {});
  }, [isSuper]);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    try {
      await api.put(`/config${qs()}`, { ...cfg, timeout: Number(cfg.timeout) || 30 });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/config/test${qs()}`);
      if (data.ok === false) toast.error(data.message);
      else toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
      loadCfg();
    }
  };

  return (
    <div className="w-full max-w-[1100px] mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Configuración de Integración API</h1>
        <p className="text-muted-foreground text-sm mt-1">Configura los parámetros de conexión al servidor externo de cada empresa, valida la disponibilidad y revisa el registro de errores.</p>
      </div>

      {isSuper && (
        <div className="mb-6 max-w-sm">
          <Label className="label-caps">Empresa</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="rounded-sm mt-2" data-testid="config-company-select"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {isSuper && !companyId ? (
        <div className="bg-card border border-dashed border-border rounded-sm py-16 text-center text-muted-foreground" data-testid="config-empty">
          Selecciona una empresa para ver y editar su configuración de API.
        </div>
      ) : (
      <>
      <div className="bg-card border border-border rounded-sm p-6 mb-6 space-y-6">
        {/* Conexión */}
        <div>
          <h2 className="font-heading font-semibold mb-4">Conexión</h2>
          <div className="space-y-5">
            <div className="max-w-xs space-y-2">
              <Label className="label-caps">Proveedor de datos</Label>
              <Select value={cfg.provider} onValueChange={(v) => setCfg((c) => ({ ...c, provider: v, auth_type: v === "g4s" && c.auth_type === "none" ? "basic" : c.auth_type }))}>
                <SelectTrigger className="rounded-sm" data-testid="config-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mock">Sistema demo (datos simulados)</SelectItem>
                  <SelectItem value="g4s">API de Ventas (Gas)</SelectItem>
                </SelectContent>
              </Select>
              {cfg.provider === "g4s" && (
                <p className="text-xs text-muted-foreground">El nombre del servidor (serverName) se toma del nombre de la empresa. La Consulta trae ventas en vivo (solo lectura) por equipo/punto de venta.</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 space-y-2">
                <Label className="label-caps">URL base de la API</Label>
                <Input value={cfg.api_url} onChange={(e) => set("api_url", e.target.value)} placeholder="https://api.servidor.com" className="rounded-sm" data-testid="config-url-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Método HTTP</Label>
                <Select value={cfg.method} onValueChange={(v) => set("method", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="config-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["GET", "POST", "PUT", "DELETE"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Proxy de salida (IP fija) — opcional</Label>
              <Input value={cfg.proxy_url} onChange={(e) => set("proxy_url", e.target.value)} placeholder="http://usuario:contraseña@ip-fija:puerto" className="rounded-sm font-mono text-xs" data-testid="config-proxy-input" />
              <p className="text-xs text-muted-foreground">Si el servidor externo usa whitelist por IP, enruta las llamadas por un proxy de IP fija (ej. QuotaGuard/Fixie) y whitelistea esa IP. Déjalo vacío para conexión directa.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 space-y-2">
                <Label className="label-caps">Endpoint / ruta del recurso</Label>
                <Input value={cfg.endpoint_path} onChange={(e) => set("endpoint_path", e.target.value)} placeholder="/v1/servicios" className="rounded-sm" data-testid="config-endpoint-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Formato de respuesta</Label>
                <Select value={cfg.response_format} onValueChange={(v) => set("response_format", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="config-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["JSON", "XML"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Autenticación */}
        <div className="border-t border-border pt-5">
          <h2 className="font-heading font-semibold mb-4">Autenticación</h2>
          <div className="space-y-5">
            <div className="max-w-xs space-y-2">
              <Label className="label-caps">Tipo de autenticación</Label>
              <Select value={cfg.auth_type} onValueChange={(v) => set("auth_type", v)}>
                <SelectTrigger className="rounded-sm" data-testid="config-auth-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin autenticación</SelectItem>
                  <SelectItem value="apikey">API Key</SelectItem>
                  <SelectItem value="bearer">Bearer Token</SelectItem>
                  <SelectItem value="basic">Basic Auth (usuario/contraseña)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {cfg.auth_type === "apikey" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="label-caps">Nombre del encabezado</Label>
                  <Input value={cfg.api_key_header} onChange={(e) => set("api_key_header", e.target.value)} placeholder="X-API-Key" className="rounded-sm" data-testid="config-apikey-header-input" />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Valor de la API Key</Label>
                  <Input value={cfg.api_key_value} onChange={(e) => set("api_key_value", e.target.value)} className="rounded-sm" data-testid="config-apikey-value-input" />
                </div>
              </div>
            )}
            {cfg.auth_type === "bearer" && (
              <div className="space-y-2">
                <Label className="label-caps">Bearer Token</Label>
                <Input value={cfg.bearer_token} onChange={(e) => set("bearer_token", e.target.value)} className="rounded-sm" data-testid="config-bearer-input" />
              </div>
            )}
            {cfg.auth_type === "basic" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="label-caps">Usuario</Label>
                  <Input value={cfg.api_username} onChange={(e) => set("api_username", e.target.value)} className="rounded-sm" data-testid="config-username-input" />
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Contraseña</Label>
                  <Input type="password" value={cfg.api_password} onChange={(e) => set("api_password", e.target.value)} className="rounded-sm" data-testid="config-password-input" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Parámetros */}
        <div className="border-t border-border pt-5">
          <h2 className="font-heading font-semibold mb-4">Parámetros de conexión</h2>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="label-caps">Encabezados personalizados (uno por línea: Clave: Valor)</Label>
              <Textarea value={cfg.headers} onChange={(e) => set("headers", e.target.value)} placeholder={"Content-Type: application/json\nAccept: application/json"} className="rounded-sm font-mono text-xs min-h-[90px]" data-testid="config-headers-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="label-caps">Parámetros de consulta (query)</Label>
                <Input value={cfg.params} onChange={(e) => set("params", e.target.value)} placeholder="key=value&key2=value2" className="rounded-sm" data-testid="config-params-input" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Timeout (segundos)</Label>
                <Input type="number" value={cfg.timeout} onChange={(e) => set("timeout", e.target.value)} className="rounded-sm" data-testid="config-timeout-input" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <Button className="rounded-sm gap-2 bg-[#DC2626] hover:bg-[#DC2626]/90 mt-5" onClick={save} data-testid="save-config-button">
            <Save className="h-4 w-4" /> Guardar
          </Button>
          <Button variant="outline" className="rounded-sm gap-2 mt-5" onClick={test} disabled={testing} data-testid="test-connection-button">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Validar conexión
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-heading font-semibold">Registro de auditoría</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Errores y pruebas de comunicación con el servidor externo.</p>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border" data-testid="logs-list">
          {logs.map((l, i) => (
            <div key={l.id || `${l.timestamp}-${i}`} className="px-6 py-3 flex items-start gap-3 text-sm">
              {l.level === "success"
                ? <CheckCircle2 className="h-4 w-4 text-[#16A34A] mt-0.5 shrink-0" />
                : <XCircle className="h-4 w-4 text-[#E11D48] mt-0.5 shrink-0" />}
              <div className="flex-1">
                <div className="break-all">{l.message}</div>
                <div className="label-caps mt-0.5">{new Date(l.timestamp).toLocaleString("es-MX")}</div>
              </div>
            </div>
          ))}
          {!logs.length && <div className="px-6 py-10 text-center text-muted-foreground text-sm">Sin registros todavía.</div>}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
